<script setup lang="ts">
  import { type PropType } from 'vue'

  import { MaterialPictureAsPdfIcon, MaterialSheetsRtlIcon, MaterialFolderZipIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
  import { Download01Icon, File06Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPText from '@ui/components/CPText.vue'

  export interface FileAttachment {
    id: string
    name: string
    url: string
    size?: number
    type?: string
    licenseId?: string
  }

  defineProps({
    /** Array of file attachments */
    attachments: {
      type: Array as PropType<FileAttachment[]>,
      required: true,
    },
    /** Show as compact list */
    compact: {
      type: Boolean,
      default: false,
    },
  })

  // Handle card click - opens file in new tab for preview
  const handleCardClick = (attachment: FileAttachment, event: Event): void => {
    // Don't trigger if clicking on download button
    if ((event.target as HTMLElement).closest('button')) {
      return
    }

    event.stopPropagation()
    // Open in new tab for preview
    window.open(attachment.url, '_blank')
  }

  // Handle download click - downloads the file
  const handleDownloadClick = async (attachment: FileAttachment, event: Event): Promise<void> => {
    event.stopPropagation()
    event.preventDefault()

    try {
      // Fetch the file
      const response = await fetch(attachment.url)
      const blob = await response.blob()

      // Create object URL
      const blobUrl = URL.createObjectURL(blob)

      // Create temporary link to trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = attachment.name || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up object URL
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Download failed:', error)
      // Fallback to opening in new tab
      window.open(attachment.url, '_blank')
    }
  }

  // Get file icon component based on extension/type
  const getFileIcon = (fileName: string, fileType?: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''

    if (fileType?.includes('pdf') || ext === 'pdf') return MaterialPictureAsPdfIcon
    if (fileType?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return MaterialSheetsRtlIcon
    if (fileType?.includes('word') || ['doc', 'docx'].includes(ext)) return File06Icon
    if (fileType?.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return File06Icon
    if (fileType?.includes('zip') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return MaterialFolderZipIcon

    return File06Icon // Generic file icon as fallback
  }

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return ''

    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`

    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`

    const gb = mb / 1024
    return `${gb.toFixed(1)} GB`
  }
</script>

<template>
  <div v-if="attachments.length > 0" :id="createElementId('docs', 'attachment', 'container')" class="file-attachments">
    <div
      v-for="attachment in attachments"
      :key="attachment.id"
      class="file-attachment-item flex cursor-pointer items-center gap-3"
      :class="{ 'mb-2 last:mb-0': attachments.length > 1 }"
      @click="handleCardClick(attachment, $event)"
    >
      <!-- File Icon -->
      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-surface text-secondary">
        <component :is="getFileIcon(attachment.name, attachment.type)" class="h-7 w-7" />
      </div>

      <!-- File Info -->
      <div :id="createElementId('docs', 'attachment', 'display')" class="min-w-0 flex-1">
        <CPText
          :id="createElementId('docs', 'attachment', attachment.name.split('.').pop() || 'unknown')"
          size="sm"
          weight="medium"
          class="block truncate text-body"
        >
          {{ attachment.name }}
        </CPText>
        <CPText v-if="attachment.size" size="xs" class="mt-0.5 text-secondary">
          {{ formatFileSize(attachment.size) }}
        </CPText>
      </div>

      <!-- Download Button -->
      <div
        class="flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg p-2 transition-colors hover:bg-hover"
        @click="handleDownloadClick(attachment, $event)"
      >
        <Download01Icon class="h-5 w-5 text-secondary hover:text-body" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .file-attachments {
    width: 100%;
  }

  .file-attachment-item {
    border: 1px solid var(--cp-border-500);
    border-radius: 8px;
    padding: 12px;
    gap: 12px;
  }
</style>
